import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  differenceInCalendarWeeks,
  endOfMonth,
  format,
  getDay,
  parseISO,
  setDate,
  startOfWeek,
} from 'date-fns';

type ReminderRecurrenceInput = {
  dueDate: string | null;
  frequencyMonths?: number | null;
  recurrenceType?: string | null;
  recurrenceInterval?: number | null;
  recurrenceUnit?: string | null;
  recurrenceDayOfWeek?: number[] | null;
  recurrenceWeekOfMonth?: string | null;
  recurrenceMonthDay?: number | null;
};

const WEEK_STARTS_ON = 1 as const;
const MAX_WEEKLY_SCAN_DAYS = 366 * 3;
const ORDINAL_INDEX: Record<string, number> = {
  first: 0,
  second: 1,
  third: 2,
  fourth: 3,
};

function toMondayIndex(date: Date) {
  return (getDay(date) + 6) % 7;
}

function clampDayOfMonth(date: Date, day: number) {
  return setDate(date, Math.min(day, endOfMonth(date).getDate()));
}

function buildWeeklyDate(baseDate: Date, interval: number, recurrenceDayOfWeek?: number[] | null) {
  const allowedDays = (recurrenceDayOfWeek?.length ? [...recurrenceDayOfWeek] : [toMondayIndex(baseDate)]).sort((left, right) => left - right);
  const baseWeek = startOfWeek(baseDate, { weekStartsOn: WEEK_STARTS_ON });

  for (let offset = 1; offset <= MAX_WEEKLY_SCAN_DAYS; offset += 1) {
    const candidate = addDays(baseDate, offset);
    if (!allowedDays.includes(toMondayIndex(candidate))) continue;

    const weekDiff = differenceInCalendarWeeks(candidate, baseWeek, { weekStartsOn: WEEK_STARTS_ON });
    if (weekDiff % interval === 0) {
      return candidate;
    }
  }

  return addWeeks(baseDate, interval);
}

function buildMonthlyWeekdayDate(
  baseDate: Date,
  interval: number,
  recurrenceWeekOfMonth: string,
  recurrenceDayOfWeek: number,
) {
  const targetMonth = addMonths(baseDate, interval);
  const year = targetMonth.getFullYear();
  const month = targetMonth.getMonth();

  if (recurrenceWeekOfMonth === 'last') {
    const lastDay = endOfMonth(new Date(year, month, 1));
    const dayOffset = (toMondayIndex(lastDay) - recurrenceDayOfWeek + 7) % 7;
    return addDays(lastDay, -dayOffset);
  }

  const ordinalIndex = ORDINAL_INDEX[recurrenceWeekOfMonth] ?? 0;
  const firstDay = new Date(year, month, 1);
  const firstOffset = (recurrenceDayOfWeek - toMondayIndex(firstDay) + 7) % 7;
  const candidate = addDays(firstDay, firstOffset + ordinalIndex * 7);

  if (candidate.getMonth() !== month) {
    return addDays(candidate, -7);
  }

  return candidate;
}

function buildYearlyDate(baseDate: Date, interval: number, monthIndex?: number | null, monthDay?: number | null) {
  const targetYear = addYears(baseDate, interval).getFullYear();
  const nextMonth = monthIndex ?? baseDate.getMonth();
  const nextDay = monthDay ?? baseDate.getDate();
  return clampDayOfMonth(new Date(targetYear, nextMonth, 1), nextDay);
}

export function getNextReminderDate(input: ReminderRecurrenceInput) {
  if (!input.dueDate) return null;

  const baseDate = parseISO(input.dueDate);
  const recurrenceInterval = input.recurrenceInterval || null;
  const recurrenceUnit = input.recurrenceUnit || null;

  if (input.recurrenceType === 'custom' && recurrenceInterval && recurrenceUnit) {
    if (recurrenceUnit === 'days') {
      return addDays(baseDate, recurrenceInterval);
    }

    if (recurrenceUnit === 'weeks') {
      return buildWeeklyDate(baseDate, recurrenceInterval, input.recurrenceDayOfWeek);
    }

    if (recurrenceUnit === 'months') {
      if (input.recurrenceWeekOfMonth && input.recurrenceDayOfWeek?.length) {
        return buildMonthlyWeekdayDate(baseDate, recurrenceInterval, input.recurrenceWeekOfMonth, input.recurrenceDayOfWeek[0]);
      }

      if (input.recurrenceMonthDay) {
        return clampDayOfMonth(addMonths(baseDate, recurrenceInterval), input.recurrenceMonthDay);
      }

      return addMonths(baseDate, recurrenceInterval);
    }

    if (recurrenceUnit === 'years') {
      return buildYearlyDate(baseDate, recurrenceInterval, input.recurrenceDayOfWeek?.[0], input.recurrenceMonthDay);
    }
  }

  if (input.frequencyMonths) {
    return addMonths(baseDate, input.frequencyMonths);
  }

  return null;
}

export function getNextReminderDueDate(input: ReminderRecurrenceInput) {
  const nextDate = getNextReminderDate(input);
  return nextDate ? format(nextDate, 'yyyy-MM-dd') : null;
}
